/**
 * Audio bus for Survey Says — mirrors the WhatIs pattern. A single shared
 * AudioContext with master/sfx/music gain nodes routes everything. Browser
 * autoplay policy keeps the context suspended until a user gesture (or OBS
 * page-permission) calls unlock().
 *
 * The legacy `destination`, `context`, and `ensureContext()` accessors are
 * preserved for any older call-sites (synth fallbacks etc).
 */

export class AudioBus {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxGainNode: GainNode | null = null;
  private musicGainNode: GainNode | null = null;
  private unlocked = false;
  private listeners = new Set<(unlocked: boolean) => void>();

  ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    if (typeof window === 'undefined') return null;
    const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!Ctor) return null;
    try {
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 1.0;
      this.master.connect(this.ctx.destination);
      this.sfxGainNode = this.ctx.createGain();
      this.sfxGainNode.gain.value = 0.7;
      this.sfxGainNode.connect(this.master);
      this.musicGainNode = this.ctx.createGain();
      this.musicGainNode.gain.value = 0.4;
      this.musicGainNode.connect(this.master);
      if (this.ctx.state === 'running') this.unlocked = true;
    } catch (err) {
      console.warn('[ss-audio-bus] AudioContext init failed:', err);
      this.ctx = null;
    }
    return this.ctx;
  }

  context(): AudioContext | null {
    return this.ensureContext();
  }

  sfxGain(): GainNode {
    this.ensureContext();
    return this.sfxGainNode!;
  }

  musicGain(): GainNode {
    this.ensureContext();
    return this.musicGainNode!;
  }

  isUnlocked(): boolean {
    return this.unlocked;
  }

  async unlock(): Promise<boolean> {
    const c = this.ensureContext();
    if (!c) return false;
    if (c.state === 'running') {
      if (!this.unlocked) {
        this.unlocked = true;
        this.notify();
      }
      return true;
    }
    try {
      await c.resume();
      const stateAfter: string = c.state;
      const ok = stateAfter === 'running';
      if (ok && !this.unlocked) {
        this.unlocked = true;
        this.notify();
      }
      return ok;
    } catch (err) {
      console.debug('[ss-audio-bus] resume blocked:', err);
      return false;
    }
  }

  onChange(cb: (unlocked: boolean) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private notify(): void {
    for (const fn of this.listeners) fn(this.unlocked);
  }

  /**
   * Legacy compatibility: some older synth-only call-sites read
   * `audioBus.destination` to grab the output node directly. Returns the
   * SFX gain node (where short cues should connect) so existing code keeps
   * routing through the bus.
   */
  get destination(): AudioNode | null {
    if (!this.ensureContext()) return null;
    return this.sfxGainNode;
  }

  setMasterVolume(v: number): void {
    this.ensureContext();
    if (this.master) this.master.gain.value = Math.max(0, Math.min(1, v));
  }
  setMusicVolume(v: number): void {
    this.ensureContext();
    if (this.musicGainNode) this.musicGainNode.gain.value = Math.max(0, Math.min(1, v));
  }
  setSfxVolume(v: number): void {
    this.ensureContext();
    if (this.sfxGainNode) this.sfxGainNode.gain.value = Math.max(0, Math.min(1, v));
  }
}

export const audioBus = new AudioBus();
